"use client";
import React, { FC } from "react";
import IconTextListItem from "@/components/TextLists/IconTextList/IconTextListItem";
import {
  CourseInformation,
  getCourseInformation,
} from "@/lib/course/utils/getCourseInformation";
import { Skeleton } from "@/components/ui/skeleton";
import { OverviewItem, Props } from "./types";
import { getProgramInformation } from "@/lib/course/utils/getProgramInformation";
import useTranslations from "@/i18n/useTranslations";

type CombinedInformation = CourseInformation & Partial<OverviewItem>;

const Loader: FC = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="flex items-center space-x-4">
        <Skeleton className="h-12 w-12 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    ))}
  </div>
);

export default function FactoidBar({
  courseData,
  paymentOption,
  hasDateColumn,
  isArchivedExternally = false,
  leadWithPaidData,
  monarchFactoidsBar,
  isFactoidsBarFieldsLoading,
  isHiddingLastElement = false,
  isHeaderFactoidBar = false,
}: Props) {
  const tCourse = useTranslations("Course");
  const { displayFactoidsIcons, isJumpLinkActive, jumpLinkUrl, urlText } =
    monarchFactoidsBar || {};

  const isMultipleCourses = Array.isArray(courseData);
  let courseInformation: CourseInformation[] = [];

  if (isMultipleCourses) {
    courseInformation = getProgramInformation(
      courseData,
      isHeaderFactoidBar,
      tCourse as unknown as (
        key: string,
        values?: Record<string, string | number | Date>,
      ) => string,
    );
  } else if (!isMultipleCourses && paymentOption) {
    courseInformation = getCourseInformation(
      courseData,
      paymentOption,
      hasDateColumn,
      isArchivedExternally,
      true,
      tCourse as unknown as (
        key: string,
        values?: Record<string, string | number | Date>,
      ) => string,
    );
  }

  const leadWithPaidTest = leadWithPaidData?.value;
  const isLoading = leadWithPaidData?.isLoading ?? false;
  const overview = leadWithPaidTest?.overview ?? {};

  let combinedCourseInformation: CombinedInformation[] = [
    ...courseInformation,
    ...Object.values(overview).map((overviewItem) => ({
      header: overviewItem.overviewHeader,
      description: overviewItem.overviewDescription ?? "",
      icon: overviewItem.overviewIcon,
      overviewLink: overviewItem.overviewLink,
    })),
  ];

  // Handle isHiddingLastElement logic first
  if (isHiddingLastElement && combinedCourseInformation.length > 0) {
    // Always delete the last item when hiding last element
    combinedCourseInformation = combinedCourseInformation.slice(0, -1);
  }

  const IconTextListItems: FC<{ factoidsData: CombinedInformation[] }> = ({
    factoidsData,
  }) =>
    factoidsData.map((item) => {
      let iconSize = "42";
      let linkClass = "";
      let iconClassName = "";
      //item has jumplink
      if (item.overviewLink) {
        iconSize = "50";
        // Use darker brand color for better contrast on light putty background
        linkClass = "text-brand-700";
        iconClassName = item.overviewIconClassName ?? "";
      }

      const selectedLink = item.overviewLink
        ? isJumpLinkActive
          ? jumpLinkUrl || item.overviewLink
          : undefined
        : undefined;

      const descriptionText = item.overviewLink
        ? urlText || item.overviewDescription || item.description
        : item.overviewDescription || item.description;

      return (
        <IconTextListItem
          key={item.icon}
          icon={
            displayFactoidsIcons ? (item.overviewIcon ?? item.icon) : undefined
          }
          header={item.overviewHeader ?? item.header}
          boldHeader={true}
          description={descriptionText}
          small
          className="pb-0"
          link={selectedLink}
          textContainerClassName="!pr-0"
          headerClassName="text-[16px]"
          descriptionClassName="text-[14px]"
          iconSize={iconSize}
          linkClassName={linkClass}
          iconClassName={iconClassName}
        />
      );
    });

  return (
    <div className="w-full bg-putty-100 border-y-8 border-x-1 border-putty-500 rounded-t-[12px] rounded-b-[12px] p-6">
      {isLoading || isFactoidsBarFieldsLoading ? (
        <Loader />
      ) : (
        <>
          <div className="md:block hidden">
            <div
              className={`flex flex-col gap-6 md:grid md:place-items-center ${
                combinedCourseInformation.length === 3
                  ? "md:grid-cols-3"
                  : "md:grid-cols-4"
              }`}
            >
              <IconTextListItems factoidsData={combinedCourseInformation} />
            </div>
          </div>

          <div className="block md:hidden">
            <div className="flex flex-col gap-6">
              <IconTextListItems factoidsData={combinedCourseInformation} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
